import React, { useState, useEffect, useCallback } from 'react';
import { History, RefreshCw, ChevronLeft, ChevronRight, Filter, X, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Action category helpers
const ACTION_COLORS = {
  LOGIN: 'text-sky-400 bg-sky-400/[0.08] border-sky-400/20',
  LOGOUT: 'text-slate-400 bg-slate-400/[0.08] border-slate-400/20',
  LOGIN_FAILED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  PASSWORD_SET: 'text-purple-400 bg-purple-400/[0.08] border-purple-400/20',
  PASSWORD_RESET: 'text-purple-400 bg-purple-400/[0.08] border-purple-400/20',
  PASSWORD_CHANGED: 'text-purple-400 bg-purple-400/[0.08] border-purple-400/20',
  INVITE_SENT: 'text-blue-400 bg-blue-400/[0.08] border-blue-400/20',
  INVITE_ACCEPTED: 'text-blue-400 bg-blue-400/[0.08] border-blue-400/20',
  ADMIN_CREATED: 'text-indigo-400 bg-indigo-400/[0.08] border-indigo-400/20',
  ADMIN_UPDATED: 'text-indigo-400 bg-indigo-400/[0.08] border-indigo-400/20',
  ADMIN_DISABLED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  CONTENT_CREATED: 'text-emerald-400 bg-emerald-400/[0.08] border-emerald-400/20',
  CONTENT_UPDATED: 'text-emerald-400 bg-emerald-400/[0.08] border-emerald-400/20',
  CONTENT_DELETED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  CONTENT_PUBLISHED: 'text-green-400 bg-green-400/[0.08] border-green-400/20',
  PAYMENT_RECEIVED: 'text-gold bg-gold/[0.08] border-gold/20',
  PAYMENT_FAILED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  PAYMENT_REFUNDED: 'text-amber-400 bg-amber-400/[0.08] border-amber-400/20',
  PAYMENT_DISPUTED: 'text-orange-400 bg-orange-400/[0.08] border-orange-400/20',
  SUBSCRIBER_ADDED: 'text-teal-400 bg-teal-400/[0.08] border-teal-400/20',
  SUBSCRIBER_REMOVED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  NURTURE_EMAIL_SENT: 'text-cyan-400 bg-cyan-400/[0.08] border-cyan-400/20',
  NURTURE_EMAIL_FAILED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  NURTURE_STAGE_CHANGED: 'text-cyan-400 bg-cyan-400/[0.08] border-cyan-400/20',
  SESSION_APPROVED: 'text-green-400 bg-green-400/[0.08] border-green-400/20',
  SESSION_REJECTED: 'text-red-400 bg-red-400/[0.08] border-red-400/20',
  WHITELIST_ADDED: 'text-teal-400 bg-teal-400/[0.08] border-teal-400/20',
  WHITELIST_REMOVED: 'text-orange-400 bg-orange-400/[0.08] border-orange-400/20',
  CRAWLER_SETTING_UPDATED: 'text-slate-400 bg-slate-400/[0.08] border-slate-400/20',
  TWO_FACTOR_ENABLED: 'text-green-400 bg-green-400/[0.08] border-green-400/20',
  TWO_FACTOR_DISABLED: 'text-orange-400 bg-orange-400/[0.08] border-orange-400/20',
};

const defaultColor = 'text-cream/40 bg-cream/[0.05] border-cream/10';

const ACTION_CATEGORY = {
  auth: ['LOGIN', 'LOGOUT', 'LOGIN_FAILED', 'PASSWORD_SET', 'PASSWORD_RESET', 'PASSWORD_CHANGED', 'TWO_FACTOR_ENABLED', 'TWO_FACTOR_DISABLED', 'TWO_FACTOR_VERIFIED', 'TWO_FACTOR_FAILED', 'BACKUP_CODE_USED', 'BACKUP_CODES_REGENERATED'],
  admin: ['INVITE_SENT', 'INVITE_ACCEPTED', 'ADMIN_CREATED', 'ADMIN_UPDATED', 'ADMIN_DISABLED', 'WHITELIST_ADDED', 'WHITELIST_REMOVED'],
  content: ['CONTENT_CREATED', 'CONTENT_UPDATED', 'CONTENT_DELETED', 'CONTENT_PUBLISHED'],
  payments: ['PAYMENT_RECEIVED', 'PAYMENT_FAILED', 'PAYMENT_REFUNDED', 'PAYMENT_DISPUTED'],
  nurture: ['SUBSCRIBER_ADDED', 'SUBSCRIBER_REMOVED', 'NURTURE_EMAIL_SENT', 'NURTURE_EMAIL_FAILED', 'NURTURE_STAGE_CHANGED'],
  sessions: ['SESSION_APPROVED', 'SESSION_REJECTED'],
};

const CATEGORY_LABELS = { auth: 'Auth', admin: 'Admin', content: 'Content', payments: 'Payments', nurture: 'Nurture', sessions: 'Sessions' };

function ActionBadge({ action }) {
  const color = ACTION_COLORS[action] || defaultColor;
  const label = action.replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-medium tracking-wide ${color}`}>
      {label}
    </span>
  );
}

function ActorBadge({ adminEmail }) {
  if (adminEmail === 'system') {
    return <span className="text-[10px] text-slate-400 bg-slate-400/[0.08] border border-slate-400/20 rounded px-1.5 py-0.5">system</span>;
  }
  return <span className="text-cream/50 text-xs truncate max-w-[160px]">{adminEmail}</span>;
}

function DetailsCell({ details, resourceType, resourceId }) {
  if (!details && !resourceId) return <span className="text-cream/20 text-xs">—</span>;
  const parts = [];
  if (resourceType) parts.push(resourceType);
  if (resourceId) parts.push(resourceId);
  if (details?.customer_email) parts.push(details.customer_email);
  if (details?.stage) parts.push(`stage ${details.stage}`);
  if (details?.amount) parts.push(`${(details.amount / 100).toFixed(2)}`);
  if (details?.title) parts.push(details.title);
  if (details?.email) parts.push(details.email);
  return <span className="text-cream/40 text-xs truncate max-w-[200px]">{parts.join(' · ')}</span>;
}

function formatTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const AdminAudit = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [allActions, setAllActions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterActor, setFilterActor] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const activeFilters = [filterAction, filterActor, filterDateFrom, filterDateTo, filterCategory].filter(Boolean).length;

  const fetchLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    const h = { Authorization: `Bearer ${token}` };
    const params = new URLSearchParams({ page: pg, page_size: 50 });
    if (filterAction) params.set('action', filterAction);
    if (filterActor) params.set('actor', filterActor);
    if (filterDateFrom) params.set('date_from', new Date(filterDateFrom).toISOString());
    if (filterDateTo) params.set('date_to', new Date(filterDateTo + 'T23:59:59').toISOString());

    try {
      const res = await fetch(`${API_URL}/api/admin/audit-logs?${params}`, { headers: h });
      if (res.ok) {
        const d = await res.json();
        setLogs(d.logs || []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 1);
        setPage(pg);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterAction, filterActor, filterDateFrom, filterDateTo]);

  const fetchActions = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    try {
      const res = await fetch(`${API_URL}/api/admin/audit-logs/actions`, { headers: h });
      if (res.ok) {
        const d = await res.json();
        setAllActions(d.actions || []);
      }
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  // Filter by category: expand to individual actions
  const handleCategoryChange = (cat) => {
    setFilterCategory(cat);
    setFilterAction('');
  };

  // When category selected, filter logs client-side for display (actions already filtered server-side or map here)
  const displayedLogs = filterCategory
    ? logs.filter(l => (ACTION_CATEGORY[filterCategory] || []).includes(l.action))
    : logs;

  const clearFilters = () => {
    setFilterAction('');
    setFilterActor('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCategory('');
  };

  return (
    <div data-testid="admin-audit">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Audit Log</h1>
          <p className="text-cream/30 text-sm mt-1">{total.toLocaleString()} total events</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (filterAction) params.set('action', filterAction);
              if (filterActor) params.set('actor', filterActor);
              if (filterDateFrom) params.set('date_from', new Date(filterDateFrom).toISOString());
              if (filterDateTo) params.set('date_to', new Date(filterDateTo + 'T23:59:59').toISOString());
              window.open(`${API_URL}/api/admin/audit-logs/export.csv?${params}`, '_blank');
            }}
            data-testid="audit-export-csv-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gold/[0.06] text-cream/40 hover:text-cream/70 hover:border-gold/15 text-xs transition-all"
          >
            <Download size={12} />
            CSV
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="audit-filter-toggle"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition-all ${showFilters ? 'border-gold/30 text-gold bg-gold/[0.06]' : 'border-gold/[0.06] text-cream/40 hover:text-cream/70 hover:border-gold/15'}`}
          >
            <Filter size={12} />
            Filters
            {activeFilters > 0 && (
              <span className="ml-1 bg-gold text-navy text-[9px] rounded-full px-1.5 py-px font-bold">{activeFilters}</span>
            )}
          </button>
          <button
            onClick={() => fetchLogs(page)}
            data-testid="audit-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gold/[0.06] text-cream/40 hover:text-cream/70 hover:border-gold/15 text-xs transition-all"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-6 bg-navy border border-gold/[0.06] rounded-xl p-4" data-testid="audit-filter-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-cream/40 text-xs font-medium uppercase tracking-wider">Filters</p>
            {activeFilters > 0 && (
              <button onClick={clearFilters} className="flex items-center gap-1 text-cream/30 hover:text-cream/60 text-xs transition-colors">
                <X size={11} /> Clear all
              </button>
            )}
          </div>

          {/* Category quick-filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => handleCategoryChange('')}
              className={`px-3 py-1 rounded-lg text-xs border transition-all ${!filterCategory ? 'border-gold/30 text-gold bg-gold/[0.06]' : 'border-gold/[0.06] text-cream/40 hover:text-cream/60'}`}
            >
              All
            </button>
            {Object.entries(CATEGORY_LABELS).map(([cat, label]) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-3 py-1 rounded-lg text-xs border transition-all ${filterCategory === cat ? 'border-gold/30 text-gold bg-gold/[0.06]' : 'border-gold/[0.06] text-cream/40 hover:text-cream/60'}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-cream/30 text-[10px] mb-1 uppercase tracking-wide">Action Type</label>
              <select
                value={filterAction}
                onChange={e => setFilterAction(e.target.value)}
                data-testid="audit-filter-action"
                className="w-full bg-navy-deep border border-gold/[0.06] text-cream/70 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-gold/20"
              >
                <option value="">All actions</option>
                {allActions.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-cream/30 text-[10px] mb-1 uppercase tracking-wide">Actor</label>
              <select
                value={filterActor}
                onChange={e => setFilterActor(e.target.value)}
                data-testid="audit-filter-actor"
                className="w-full bg-navy-deep border border-gold/[0.06] text-cream/70 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-gold/20"
              >
                <option value="">All actors</option>
                <option value="human">Human admin</option>
                <option value="system">System (automated)</option>
              </select>
            </div>
            <div>
              <label className="block text-cream/30 text-[10px] mb-1 uppercase tracking-wide">From</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                data-testid="audit-filter-date-from"
                className="w-full bg-navy-deep border border-gold/[0.06] text-cream/70 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-gold/20"
              />
            </div>
            <div>
              <label className="block text-cream/30 text-[10px] mb-1 uppercase tracking-wide">To</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                data-testid="audit-filter-date-to"
                className="w-full bg-navy-deep border border-gold/[0.06] text-cream/70 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-gold/20"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw size={24} className="mx-auto text-cream/10 animate-spin mb-3" />
            <p className="text-cream/30 text-sm">Loading audit log…</p>
          </div>
        ) : displayedLogs.length === 0 ? (
          <div className="p-12 text-center">
            <History size={32} className="mx-auto text-cream/10 mb-3" />
            <p className="text-cream/40 text-sm">No audit log entries found</p>
            {activeFilters > 0 && (
              <button onClick={clearFilters} className="mt-3 text-gold/60 hover:text-gold text-xs transition-colors">Clear filters</button>
            )}
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="grid grid-cols-[140px_1fr_160px_1fr_80px] gap-x-4 px-5 py-2.5 border-b border-gold/[0.06] text-[10px] text-cream/25 uppercase tracking-wider">
              <span>Time</span>
              <span>Action</span>
              <span>Actor</span>
              <span>Details</span>
              <span className="text-right">Resource</span>
            </div>
            <div data-testid="audit-log-table">
              {displayedLogs.map((log, i) => (
                <div
                  key={log.id || i}
                  data-testid={`audit-row-${i}`}
                  className="grid grid-cols-[140px_1fr_160px_1fr_80px] gap-x-4 px-5 py-3 border-b border-gold/[0.03] hover:bg-cream/[0.01] transition-colors items-center"
                >
                  <span className="text-cream/30 text-xs font-mono">{formatTs(log.timestamp)}</span>
                  <div><ActionBadge action={log.action} /></div>
                  <div className="min-w-0"><ActorBadge adminEmail={log.admin_email} /></div>
                  <div className="min-w-0 overflow-hidden">
                    <DetailsCell details={log.details} resourceType={log.resource_type} resourceId={log.resource_id} />
                  </div>
                  <span className="text-cream/20 text-[10px] text-right truncate">{log.resource_type || '—'}</span>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gold/[0.06]">
                <span className="text-cream/25 text-xs">Page {page} of {totalPages} · {total.toLocaleString()} total</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fetchLogs(page - 1)}
                    disabled={page <= 1}
                    data-testid="audit-prev-page"
                    className="p-1.5 rounded-lg border border-gold/[0.06] text-cream/40 hover:text-cream/70 hover:border-gold/15 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={() => fetchLogs(page + 1)}
                    disabled={page >= totalPages}
                    data-testid="audit-next-page"
                    className="p-1.5 rounded-lg border border-gold/[0.06] text-cream/40 hover:text-cream/70 hover:border-gold/15 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAudit;
